import React, { useState } from 'react';
import {
  Grid,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  CircularProgress,
  TextField,
  Card,
  CardContent,
  CardHeader,
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { useApiGet, useApiSend } from '../../utils/httpClientTs';
import {
  getLoans,
  applyPayment,
  type LoanSummary,
} from '../../endpoints/loans';

const LoansTestUserDetail: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const [paymentAmount, setPaymentAmount] = useState('');
  const [postingDate, setPostingDate] = useState(
    new Date().toISOString().slice(0, 10)
  );
  const [paymentMethod, setPaymentMethod] = useState('check');
  const [actionError, setActionError] = useState<string | null>(null);

  const params = userId ? { user_id: userId } : undefined;
  const {
    data: loans,
    isLoading,
    error,
    refetch,
  } = useApiGet<LoanSummary[]>(
    ['loans', 'user', userId ?? ''],
    () => getLoans(params!),
    { enabled: !!userId }
  );

  const { mutate: doApplyPayment, isPending: paymentPending } = useApiSend(
    (body: {
      amount: number;
      posting_date: string;
      payment_method: string;
      user_id?: string;
    }) => applyPayment(body),
    () => {
      setPaymentAmount('');
      setActionError(null);
      refetch();
    },
    (err: Error) =>
      setActionError((err as any)?.response?.data?.error ?? err.message),
    [['loans', 'user', userId]]
  );

  if (!userId) return <Typography>Missing user ID</Typography>;
  if (isLoading) return <CircularProgress />;
  if (error) {
    return (
      <Typography color="error">
        {(error as any)?.response?.data?.error ??
          (error as Error)?.message ??
          'Failed to load loans'}
      </Typography>
    );
  }

  const list = loans ?? [];

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Button
          size="small"
          onClick={() => navigate('/administration/loans-test')}
        >
          ← Back to loans list
        </Button>
        <Typography variant="h5" sx={{ mt: 1 }}>
          Loans for user {userId}
        </Typography>
      </Grid>

      <Grid item xs={12} md={4}>
        <Card>
          <CardHeader title="Apply payment" />
          <CardContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Payment will be applied to this user&apos;s loans.
            </Typography>
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <TextField
                  size="small"
                  type="number"
                  inputProps={{ min: 0.01, step: 0.01 }}
                  label="Amount"
                  value={paymentAmount}
                  onChange={(e) => setPaymentAmount(e.target.value)}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  size="small"
                  type="date"
                  label="Posting date"
                  value={postingDate}
                  onChange={(e) => setPostingDate(e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  size="small"
                  label="Method"
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  size="small"
                  disabled={
                    !paymentAmount ||
                    Number(paymentAmount) <= 0 ||
                    paymentPending
                  }
                  onClick={() =>
                    doApplyPayment({
                      amount: Number(paymentAmount),
                      posting_date: postingDate,
                      payment_method: paymentMethod,
                      user_id: userId,
                    })
                  }
                >
                  {paymentPending ? 'Applying…' : 'Apply payment'}
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      {actionError && (
        <Grid item xs={12}>
          <Typography color="error">{actionError}</Typography>
        </Grid>
      )}

      <Grid item xs={12}>
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Principal</TableCell>
                <TableCell align="right">Interest</TableCell>
                <TableCell align="right">Charges</TableCell>
                <TableCell>Next payment</TableCell>
                <TableCell>First payment</TableCell>
                <TableCell>Start date</TableCell>
                <TableCell>Anniversary</TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {list.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11}>No loans for this user.</TableCell>
                </TableRow>
              ) : (
                list.map((loan) => (
                  <TableRow key={loan.id} hover>
                    <TableCell
                      sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
                    >
                      {loan.id.slice(0, 8)}…
                    </TableCell>
                    <TableCell align="right">
                      {loan.amount.toFixed(2)}
                    </TableCell>
                    <TableCell>{loan.status}</TableCell>
                    <TableCell align="right">
                      {loan.current_principal_balance.toFixed(2)}
                    </TableCell>
                    <TableCell align="right">
                      {loan.current_interest_balance.toFixed(2)}
                    </TableCell>
                    <TableCell align="right">
                      {loan.current_charges_balance.toFixed(2)}
                    </TableCell>
                    <TableCell>{loan.next_payment_date ?? '—'}</TableCell>
                    <TableCell>{loan.first_payment_date}</TableCell>
                    <TableCell>{loan.start_date ?? '—'}</TableCell>
                    <TableCell>{loan.anniversary_date ?? '—'}</TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        onClick={() =>
                          navigate(`/administration/loans-test/${loan.id}`)
                        }
                      >
                        Manage
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>
    </Grid>
  );
};

export default LoansTestUserDetail;
